import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack, useMediaQuery} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {DateTimePicker} from '@mui/x-date-pickers';
import moment from 'moment/moment';
import {useTheme} from '@mui/material/styles';
import {useLocation} from 'react-router-dom';

const DateTime = ({
                      index,
                      componentId,
                      value,
                      onChange,
                      descriptionHtml,
                      onDescriptionHtmlChange,
                      commitDescriptionHtml,
                      enableValidation,
                      isEditMode,
                      isFormArchived,
                      readOnly,
                      placeholderValues,
                      accessLevel,
                      submitMode,
                      minDateTime,
                      maxDateTime,
                      error,
                      audience,
                      recipientType,
                      linkedToStudents,
                  }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const dateTimePickerRef = useRef(null);
    const theme = useTheme();
    const location = useLocation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isEmbedded = new URLSearchParams(location.search).get('embedded') === 'true';
    const desktopModeMediaQuery = isEmbedded ? '@media (min-width: 0px)' : '@media (pointer: fine)';
    const embeddedSmallScreenDateTimeSlotProps = isEmbedded && isSmallScreen
        ? {
            popper: {
                placement: 'bottom-end',
                modifiers: [
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            fallbackPlacements: ['top-end', 'bottom-start', 'top-start']
                        }
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altAxis: true,
                            tether: true,
                            padding: 8
                        }
                    }
                ]
            },
            desktopPaper: {
                sx: {
                    maxWidth: 'calc(100vw - 16px)',
                    overflowX: 'auto'
                }
            }
        }
        : {};

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback(
        (nextDescriptionHtml) => {
            setDraftDescriptionHtml(nextDescriptionHtml);
            onDescriptionHtmlChange(nextDescriptionHtml);
        },
        [onDescriptionHtmlChange]
    );

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const formatDateTime = (date) => {
        return moment(date).format('D MMM YYYY HH:mm');
    };

    const handleOpen = () => {
        setIsOpen(true);
        if (!isSmallScreen) {
            setTimeout(() => {
                if (dateTimePickerRef.current) {
                    dateTimePickerRef.current.scrollIntoView({
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 10);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            gap={3}
                            mb={2}
                        >
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived}
                            />
                        </Stack>
                        <Stack flexDirection="row" gap={2} mb={2}>
                            <DateTimePicker
                                label="Min Date Time"
                                desktopModeMediaQuery={desktopModeMediaQuery}
                                value={minDateTime ? moment(minDateTime) : null}
                                onChange={(date) =>
                                    onChange({
                                        index,
                                        minDateTime: date ? date.toDate() : undefined,
                                    })
                                }
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!error,
                                        helperText: error,
                                    },
                                    ...embeddedSmallScreenDateTimeSlotProps,
                                }}
                            />
                            <DateTimePicker
                                label="Max Date Time"
                                desktopModeMediaQuery={desktopModeMediaQuery}
                                value={maxDateTime ? moment(maxDateTime) : null}
                                onChange={(date) =>
                                    onChange({
                                        index,
                                        maxDateTime: date ? date.toDate() : undefined,
                                    })
                                }
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!error,
                                        helperText: error,
                                    },
                                    ...embeddedSmallScreenDateTimeSlotProps,
                                }}
                            />
                        </Stack>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={draftDescriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                        {!readOnly
                            ? (
                                <DateTimePicker
                                    key={componentId}
                                    ref={dateTimePickerRef}
                                    desktopModeMediaQuery={desktopModeMediaQuery}
                                    open={isOpen}
                                    onOpen={handleOpen}
                                    onClose={handleClose}
                                    value={value ? moment(value) : null}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!error,
                                            helperText: error
                                        },
                                        field: {readOnly: true},
                                        ...embeddedSmallScreenDateTimeSlotProps,
                                    }}
                                    format="D MMM YYYY HH:mm"
                                    ampm={false}
                                    required={enableValidation}
                                    onChange={(newValue) => {
                                        onChange({
                                            id: componentId,
                                            value: newValue ? formatDateTime(newValue) : null,
                                        });
                                    }}
                                    onAccept={() => {
                                        setIsOpen(false);
                                    }}
                                    disabled={!submitMode}
                                    minDateTime={minDateTime ? moment(minDateTime) : undefined}
                                    maxDateTime={maxDateTime ? moment(maxDateTime) : undefined}
                                />
                            )
                            : (
                                <Typography
                                    sx={{
                                        width: {
                                            xs: '80vw',
                                            sm: '60vw',
                                            md: '45vw',
                                            lg: '45vw',
                                            xl: '45vw',
                                        },
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    {value && formatDateTime(value)}
                                </Typography>
                            )}
                    </>
                )}
        </Box>
    );
};

DateTime.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    minDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
};

export default DateTime;
